import {
  ApiError,
  DailySheetLayoutController_read_v1,
  DailySheetLayoutController_replace_v1,
  type DailySheetLayoutResource,
  type OutletResource,
  OutletsController_list_v1,
  type SheetOutletResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowRight,
  IconArrowUp,
  IconCircleCheck,
  IconPlus,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isNotFound, messageForFailure } from '@/api/failure';
import { useSession } from '@/auth/session';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';

import { LoadFailed, Screen, ScreenNote } from '../back-office/Screen';
import { paths } from '../paths';

/**
 * The daily sheet's blocks (#276): `GET` and `PUT /v1/daily-sheet-layout`.
 * Everyone who reads the sheet reads this; only the owner arranges it.
 *
 * **The grouping is stored configuration, never derived.** Chair Massage is an
 * `ACTIVITY` in our data and sits in the owner's *shops* block — any rule from
 * outlet kind would place it wrongly. So nothing here suggests a block.
 *
 * **The `PUT` replaces the whole layout**, so Save sends every block. It is
 * saved repeatedly with a different arrangement each time, so the key is per
 * attempt — the `Staffing` case in `docs/ui-conventions.md`, and safe for the
 * same reason: it replaces a set rather than adding to one.
 *
 * An outlet in no block is listed as not placed; its money stays in every
 * total on the sheet. An empty block is not kept — the API takes at least one
 * outlet per block — so it is dropped when saved.
 */

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded' }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The blocks, top of the page first, each its outlets' ids top to bottom. */
type Blocks = readonly (readonly string[])[];

const NOT_PLACED = 'NOT_PLACED';

const DUPLICATE_CONSTRAINT = 'must not name an outlet more than once';

const blocksOf = (layout: DailySheetLayoutResource): Blocks =>
  layout.groups.map((group) => group.outlets.map((outlet) => outlet.id));

const blockLabel = (index: number) => `Block ${String(index + 1)}`;

export function SheetLayout() {
  const client = useApiClient();
  const { session } = useSession();

  // Courtesy, not security: the `PUT` is `@RequireRole('OWNER')`.
  const mayArrange = session?.user.role === 'OWNER';

  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [outlets, setOutlets] = useState<ReadonlyMap<string, SheetOutletResource>>(new Map());
  const [trading, setTrading] = useState<readonly string[]>([]);
  const [saved, setSaved] = useState<Blocks>([]);
  const [blocks, setBlocks] = useState<Blocks>([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setLoad({ kind: 'loading' });

    Promise.all([
      client.request(DailySheetLayoutController_read_v1, { signal: controller.signal }),
      // Trading outlets, few enough to read whole: the ones that can be placed.
      collectPages<OutletResource>((offset, limit) =>
        client.request(OutletsController_list_v1, {
          query: { offset, limit },
          signal: controller.signal,
        }),
      ),
    ])
      .then(([layout, all]) => {
        if (!live) return;

        // A placed outlet may have retired since, so the layout's own names
        // count as well as the trading list's.
        const byId = new Map<string, SheetOutletResource>();
        for (const group of layout.groups) {
          for (const outlet of group.outlets) byId.set(outlet.id, outlet);
        }
        for (const { id, code, name } of all) byId.set(id, { id, code, name });

        setOutlets(byId);
        setTrading(all.map((outlet) => outlet.id));
        setSaved(blocksOf(layout));
        setBlocks(blocksOf(layout));
        setLoad({ kind: 'loaded' });
      })
      .catch((failure: unknown) => {
        if (live && !(failure instanceof DOMException && failure.name === 'AbortError')) {
          setLoad({ kind: 'failed', error: failure });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, reloads]);

  const placed = new Set(blocks.flat());
  const unplaced = trading.filter((id) => !placed.has(id));
  const changed = JSON.stringify(blocks) !== JSON.stringify(saved);

  const destinations = useMemo(
    () => [
      ...blocks.map((_, index) => ({ label: blockLabel(index), value: String(index) })),
      { label: 'Not placed', value: NOT_PLACED },
    ],
    [blocks],
  );

  const arrange = (next: Blocks) => {
    setBlocks(next);
    setDone(false);
    setError(null);
  };

  /** Takes `id` out of wherever it is and, unless `to` is `NOT_PLACED`, appends it to block `to`. */
  const moveTo = (id: string, to: string) => {
    const without = blocks.map((block) => block.filter((candidate) => candidate !== id));

    arrange(
      to === NOT_PLACED
        ? without
        : without.map((block, index) => (String(index) === to ? [...block, id] : block)),
    );
  };

  /** One place up or down inside its own block. */
  const shift = (blockIndex: number, position: number, step: -1 | 1) => {
    arrange(
      blocks.map((block, index) => {
        if (index !== blockIndex) return block;

        const next = [...block];
        const [taken] = next.splice(position, 1);
        if (taken !== undefined) next.splice(position + step, 0, taken);

        return next;
      }),
    );
  };

  const handleSave = async () => {
    if (saving) return;

    setSaving(true);
    setError(null);
    setDone(false);

    const groups = blocks.filter((block) => block.length > 0);

    try {
      const answer = await client.request(DailySheetLayoutController_replace_v1, {
        body: { groups: groups.map((outletIds) => ({ outletIds: [...outletIds] })) },
        headers: { [IDEMPOTENCY_KEY_HEADER]: newIdempotencyKey() },
      });

      setSaved(blocksOf(answer));
      setBlocks(blocksOf(answer));
      setDone(true);
    } catch (failure) {
      setError(layoutRefusal(failure));
    } finally {
      setSaving(false);
    }
  };

  const nameOf = (id: string): ReactNode => {
    const outlet = outlets.get(id);

    return outlet === undefined ? (
      <span className="font-mono numeric">{id}</span>
    ) : (
      <>
        <span className="me-2 font-mono text-xs text-muted-foreground numeric">{outlet.code}</span>
        {outlet.name}
      </>
    );
  };

  const plainName = (id: string) => outlets.get(id)?.name ?? id;

  const destination = (id: string, value: string, label: string) => (
    <Select
      value={value}
      items={destinations}
      onValueChange={(next) => {
        if (next !== null && next !== value) moveTo(id, next);
      }}
    >
      <SelectTrigger aria-label={`${label} ${plainName(id)}`} className="h-9 w-36 bg-background">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {destinations.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );

  let body: ReactNode;

  if (load.kind === 'loading') {
    body = (
      <div aria-hidden className="space-y-3">
        {Array.from({ length: 6 }, (_, row) => (
          <div key={row} className="h-4 animate-pulse rounded-3xl bg-muted" />
        ))}
      </div>
    );
  } else if (load.kind === 'failed') {
    body = (
      <div className="space-y-3 text-center">
        <LoadFailed error={load.error} onRetry={reload} title="The layout could not be loaded" />
      </div>
    );
  } else {
    body = (
      <div className="space-y-8">
        {blocks.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No blocks yet, so every outlet is listed apart on the sheet.
            {mayArrange && ' Add a block and place outlets in it in the order the book lists them.'}
          </p>
        )}

        {blocks.map((block, blockIndex) => (
          <section key={blockIndex} aria-label={blockLabel(blockIndex)}>
            <h2 className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
              {blockLabel(blockIndex)}
            </h2>

            {block.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                Empty — it is dropped when the layout is saved.
              </p>
            ) : (
              <ol className="mt-2 divide-y divide-border border-y border-border">
                {block.map((id, position) => (
                  <li key={id} className="flex items-center gap-3 py-2">
                    <span className="min-w-0 flex-1 truncate">{nameOf(id)}</span>
                    {mayArrange && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Move ${plainName(id)} up`}
                          disabled={position === 0}
                          onClick={() => shift(blockIndex, position, -1)}
                        >
                          <IconArrowUp />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Move ${plainName(id)} down`}
                          disabled={position === block.length - 1}
                          onClick={() => shift(blockIndex, position, 1)}
                        >
                          <IconArrowDown />
                        </Button>
                        {destination(id, String(blockIndex), 'Block for')}
                      </>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </section>
        ))}

        {mayArrange && (
          <Button variant="outline" onClick={() => arrange([...blocks, []])}>
            <IconPlus data-icon="inline-start" />
            Add a block
          </Button>
        )}

        <section aria-label="Not placed">
          <h2 className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
            Not placed
          </h2>

          {unplaced.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Every trading outlet is placed.</p>
          ) : (
            <ul className="mt-2 divide-y divide-border border-y border-border">
              {unplaced.map((id) => (
                <li key={id} className="flex items-center gap-3 py-2">
                  <span className="min-w-0 flex-1 truncate">{nameOf(id)}</span>
                  {mayArrange && blocks.length > 0 && destination(id, NOT_PLACED, 'Place')}
                </li>
              ))}
            </ul>
          )}
        </section>

        {error !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {done && (
          <Alert role="status">
            <IconCircleCheck />
            <AlertDescription>
              Saved. The sheet draws its outlets in this order now.
            </AlertDescription>
          </Alert>
        )}

        {mayArrange && (
          <div className="mt-8">
            <Button disabled={saving || !changed} onClick={() => void handleSave()}>
              {saving ? 'Saving…' : 'Save layout'}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <Screen
      title="Sheet layout"
      description="The blocks the daily sheet draws its outlets in, top of the page first, as the park’s book lists them. It changes where a row is drawn and never what is added up."
      guidance={
        <>
          <ScreenNote label="Set by hand">
            The blocks are the park’s own habit, not anything an outlet’s kind decides — Chair
            Massage is an activity and sits with the shops.
          </ScreenNote>

          <ScreenNote label="Not placed">
            An outlet in no block is listed apart on the sheet on any day it has a figure. Its money
            is in every total either way.
          </ScreenNote>

          {!mayArrange && <ScreenNote label="Read only">The owner arranges the blocks.</ScreenNote>}

          <div className="space-y-2 border-t border-border pt-4 text-sm">
            <Link
              to={paths.dailySheet}
              className="inline-flex items-center gap-1.5 rounded-3xl text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
            >
              The daily sheet
              <IconArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
        </>
      }
    >
      {body}
    </Screen>
  );
}

/** The two refusals a layout can earn, worded; anything else by its code. */
function layoutRefusal(failure: unknown): string {
  if (isNotFound(failure)) {
    return 'One of these outlets no longer exists. Reload the page and arrange the blocks again. Nothing was saved.';
  }

  if (failure instanceof ApiError && failure.code === 'VALIDATION_FAILED') {
    const fields = failure.details?.['fields'];
    const duplicate =
      Array.isArray(fields) &&
      fields.some(
        (field: unknown) =>
          typeof field === 'object' &&
          field !== null &&
          'constraint' in field &&
          field.constraint === DUPLICATE_CONSTRAINT,
      );

    if (duplicate) {
      return 'An outlet is named in more than one place. Each outlet sits in one block at most. Nothing was saved.';
    }
  }

  return messageForFailure(failure);
}
